import { useState } from "react";
import { useCustomer } from "../../../../layouts/SelfcareLayout.tsx";
import {FlowData, ProductInstance, SELFCARE_REPORT_FLOW_TYPE} from "../../../../util/types.ts";
import ConnectionTypeComponent from "../FormElements/ConnectionTypeComponent.tsx";
import DeviceSelectionError from "../FormElements/DeviceSelectionErrorComponent.tsx";
import DescriptionTextFieldComponent from "../FormElements/DescriptionTextFieldComponent.tsx";
import ContactInformationComponent from "../FormElements/ContactInformationComponent.tsx";
import FormFieldsetComponent from "../FormElements/FormFieldsetComponent.tsx";
import OwnHardwareComponent from "../FormElements/OwnHardwareComponent.tsx";
import Icon from "../../Icon.tsx";
import ErrorFlowButtons from "../FormElements/ErrorFlowButtonsComponent.tsx";
import {DispatchResetErrorReportPageEvent} from "../../../../util/events.ts";
import IconOpenInNew from "../../../icons/IconOpenInNew.tsx";
import {submitFormFunction} from "../../../../util/SelfcareErrorReportTypes.ts";
import {
    anyComponentsWithValidationError,
    filterOnProductsThatCanSeeTheNetwork,
    registerComponentsWithValidation
} from "../../../../util/helpers.ts";
import AttachFilesComponent from "../FormElements/AttachFilesComponent.tsx";
import {Link} from "react-router-dom";
import Routing from "../../../../Routing.ts";

export default function OtherUnstableFlow({product, submitFunction}: { product: ProductInstance, submitFunction: submitFormFunction }) {
    const { customer } = useCustomer();

    const filtered_laks_products = filterOnProductsThatCanSeeTheNetwork(customer.product_instances)

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string>('');
    const flowType = SELFCARE_REPORT_FLOW_TYPE.UNSTABLE;

    const [flowData, setFlowData] = useState<FlowData>({
        flowtype: flowType,
        technology: product.product.att_statistics_technology,
        email: customer.customer_contact.email,
        phone: customer.customer_contact.mobile_no,
        selectedContact: { email: false, phone: false },
        editingContact: {email: false, phone: false},
        devices: [],
        otherDeviceDescription: '',
        descriptionText: '',
        connectionType: '',
        ownHardware: null,
        ownHardwareDescription: '',
        attachedFiles: [] as File[],
    });

    const handleSubmit = async () => {
        if (anyComponentsWithValidationError(flowData)) {
            return;
        }

        setSubmitting(true)
        submitFunction(customer.customer_id, product.product_instance_id, flowData)
            .then((result) => {
                setSubmitError(result)
                setSubmitting(false)
            })
    };

    return (
        <>
            <FormFieldsetComponent
                number={2}
                title={'Inden du fejlmelder'}
                className={'before-you-start'}
            >
                <BeforeYouStart
                    filtered_laks_products={filtered_laks_products}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={3}
                title={'Opsætning'}
                className={'equipment-setup'}
            >
                <ConnectionTypeComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    flowtype={flowType}
                    register={registerComponentsWithValidation}
                />

                <DeviceSelectionError
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />

                <OwnHardwareComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={4}
                title={'Uddyb'}
                className={'explain'}
            >
                <DescriptionTextFieldComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    flowtype={flowType}
                    placeholder={'Eks. "Jeg oplever problemer når jeg streamer", "Jeg oplever udfald ved gaming på PlayStation", "Problemerne opstår i tidsrummet kl. 18-20"'}
                    register={registerComponentsWithValidation}
                />
                <AttachFilesComponent
                    setFlowData={setFlowData}
                    product={product}
                    flow={SELFCARE_REPORT_FLOW_TYPE.OFFLINE}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={5}
                title={'Kontakt'}
                className={'contact'}
            >
                <ContactInformationComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
             </FormFieldsetComponent>
            {submitError.length > 0 &&
                <div className={`input-container error`}>
                    <p className={'error-message'}>{submitError}</p>
                </div>
            }
            <ErrorFlowButtons
                onBack={() => DispatchResetErrorReportPageEvent()}
                onSubmit={handleSubmit}
                loading={submitting}
            />
        </>
    );
}

function BeforeYouStart({ filtered_laks_products }: { filtered_laks_products: ProductInstance[] }) {
    return (
        <div className={'input-container'}>
            {filtered_laks_products.length > 0 ? (
                <>
                    <p>
                        Tjek gerne wifi-signalstyrken på dine enheder, inden du fejlmelder.
                        Måske befinder nogle af dine enheder sig i et rum med begrænset wifi-dækning.
                        Du kan tjekke signalstyrken på <strong>Internet-siden</strong> under <strong>Wifi-indstillinger og -oversigt</strong>.
                    </p>
                    <p className={'align-center'}>
                        <Link
                            className={'button button--montserrat teal rounded'}
                            to={Routing.Selfcare.Internet}
                        >
                            Tilbage til Internet-siden
                        </Link>
                    </p>
                </>
            ) : (
                <>
                    <p>
                        Vi anbefaler, at du følger guiden i nedenstående hjælpeartikel, før du fejlmelder din forbindelse:
                    </p>
                    <p className={'align-center'}>
                        <Link
                            className={'button button--montserrat teal rounded with-icon'}
                            to={'/hjaelp/internet-og-wifi/ustabilt-internet'}
                            target={'_blank'}
                        >
                            <span className={'text'}>
                                Guide til ustabilt internet
                            </span>
                            <Icon component={IconOpenInNew}/>
                        </Link>
                    </p>
                </>
            )}
        </div>
    )
}
